<script lang="ts">
  import Modal from '../base/Modal.svelte';
  import Button from '../base/Button.svelte';
  import CurrencyPanel from '../base/CurrencyPanel.svelte';
  import CostDisplay from '../base/CostDisplay.svelte';
  import { Upgrades } from '@game/database';
  import { profileStore } from '@stores/game/profile';
  import { animations } from '@utils/animations';

  let { isOpen = $bindable(false), onUpgradePurchased = () => {}, onClose = () => {} } = $props();

  // Подписываемся на изменения профиля для реактивности
  const profile = $derived($profileStore);
  //const credits = $derived(profile?.inventory?.items?.credits?.quantity || 0);

  // Триггер для принудительного обновления апгрейдов
  let upgradeTrigger = $state(0);

  // Получаем только разблокированные апгрейды с дополнительной информацией
  const allUpgrades = $derived.by(() => {
    // Заставляем пересчитываться при изменении профиля и триггера
    profile;
    upgradeTrigger;

    return Upgrades.getUpgradeIds()
      .map(upgradeId => {
        const config = Upgrades.getUpgradeConfig(upgradeId);
        const purchaseInfo = Upgrades.getUpgradePurchaseInfo(upgradeId);

        if (!config || !purchaseInfo) return null;

        return {
          id: upgradeId,
          config,
          purchaseInfo,
          currentLevel: purchaseInfo.currentLevel,
          isUnlocked: purchaseInfo.isUnlocked,
          isMaxLevel: purchaseInfo.isMaxLevel,
          canAfford: purchaseInfo.canAfford,
          cost: purchaseInfo.cost,
          label: getUpgradeLabel(upgradeId),
          description: getUpgradeDescription(upgradeId, config),
          effectText: getUpgradeEffectText(upgradeId, purchaseInfo.currentLevel, config),
        };
      })
      .filter(
        (upgrade): upgrade is NonNullable<typeof upgrade> => upgrade !== null && upgrade.isUnlocked
      );
  });

  // Функции для получения читаемых названий и описаний
  function getUpgradeLabel(upgradeId: string): string {
    // Используем название из конфига апгрейда
    const upgradeConfig = Upgrades.getUpgradeConfig(upgradeId);
    if (upgradeConfig?.name) {
      return upgradeConfig.name;
    }

    // Fallback к старым названиям
    const labels: Record<string, string> = {
      u_laser_base: 'Laser: Base Power',
      u_laser_radius: 'Laser Radius',
      u_laser_crit_ch1: 'Laser Crit Chance I',
      u_craft_double: 'Double Craft Chance',
      u_rock_triple: 'Triple Rock Chance',
      u_bomb_dmg1: 'Bomb Damage I',
      u_ore_sell: 'Ore Sell Price',
      u_laser_crit_dmg1: 'Laser Crit Damage I',
      u_laser_dmg10: 'Laser Damage I',
      u_bomb_free: 'Free Bomb Chance',
      u_upg_gold_disc: 'Upgrade Gold Prices ↓',
      u_global_crit_ch: 'Global Crit Chance',
      u_laser_dmg15: 'Laser Damage II',
      u_laser_spd2a: 'Laser Attack Speed I',
      u_laser_crit_dmg12: 'Laser Crit Damage II',
      u_bomb_recharge: 'Bomb Recharge Rate',
      u_laser_dmg20a: 'Laser Damage III',
      u_bomb_dmg40: 'Bomb Damage II',
      u_bomb_crit_dmg8: 'Bomb Crit Damage I',
      u_laser_supercrit_ch1: 'Laser Super Crit Chance',
      u_laser_spd2b: 'Laser Attack Speed II',
      u_upg_caps: 'Increase Upgrade Caps',
      u_laser_dmg28: 'Laser Damage IV',
      u_bomb_crit_dmg12: 'Bomb Crit Damage II',
      u_laser_supercrit_dmg10: 'Laser Super Crit Damage',
      u_laser_crit_ch2: 'Laser Crit Chance II',
      u_laser_dmg35: 'Laser Damage V',
      u_obelisk_armor: 'Obelisk Armor ↓',
      u_laser_ultracrit_ch: 'Laser Ultra Crit Chance',
      u_meter_fill: 'Meter Fill Rate',
      u_bomb_ultracrit_ch: 'Bomb Ultra Crit Chance',
      u_craft_triple: 'Triple Craft Chance',
      u_laser_dmg_per_card: 'Laser Dmg per Card',
    };
    return labels[upgradeId] || upgradeId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  function getUpgradeDescription(upgradeId: string, config: any): string {
    const effectType = config.effect_type_stat_key;

    // Получаем описание из конфига или используем стандартные
    if (config.notes && config.notes !== '–') {
      return config.notes;
    }

    switch (effectType) {
      case 'laser_flat_base':
        return `Increases laser damage per level`;
      case 'laser_mult_base':
      case 'laser_dmg_mult':
        return `Increases laser damage multiplier per level`;
      case 'mine_radius_mult':
        return `Increases mining radius per level`;
      case 'crit_chance':
        return `Increases critical hit chance per level`;
      case 'crit_mult_bonus':
        return `Increases critical hit multiplier per level`;
      case 'craft_double_chance':
        return `Increases craft double chance per level`;
      case 'craft_speed_mult':
        return `Increases craft speed per level`;
      case 'triple_rock_chance':
        return `Increases triple rock chance per level`;
      case 'ore_sell_mult':
        return `Increases ore sell value per level`;
      case 'bomb_dmg_mult':
        return `Increases bomb damage per level`;
      case 'bomb_radius_mult':
        return `Increases bomb radius per level`;
      case 'energy_max':
        return `Increases max energy per level`;
      case 'energy_regen_mult':
        return `Increases energy regeneration per level`;
      case 'coin_mult':
        return `Increases coin gain per level`;
      case 'exp_mult':
        return `Increases experience gain per level`;
      case 'attack_speed':
        return `Increases attack speed per level`;
      case 'supercrit_chance':
        return `Increases super critical hit chance per level`;
      case 'supercrit_mult_bonus':
        return `Increases super critical hit damage per level`;
      case 'ultracrit_chance':
        return `Increases ultra critical hit chance per level`;
      case 'bomb_ultracrit_chance':
        return `Increases bomb ultra critical hit chance per level`;
      case 'bomb_crit_mult_bonus':
        return `Increases bomb critical hit damage per level`;
      case 'craft_triple_chance':
        return `Increases triple craft chance per level`;
      case 'laser_dmg_per_card':
        return `Increases laser damage per card per level`;
      case 'upgrade_coin_cost_mult':
        return `Reduces upgrade coin costs per level`;
      case 'enemy_armor_reduction':
        return `Reduces enemy armor per level`;
      case 'meter_fill_rate_mult':
        return `Increases meter fill rate per level`;
      case 'upg_cap_plus':
        return `Increases upgrade caps per level`;
      case 'bomb_recharge_rate':
        return `Increases bomb recharge rate per level`;
      case 'free_bomb_chance':
        return `Increases free bomb chance per level`;
      default:
        return `Upgrade ${upgradeId}`;
    }
  }

  function getUpgradeEffectText(upgradeId: string, currentLevel: number, config: any): string {
    const effectType = config.effect_type_stat_key;
    const totalEffect = Upgrades.calculateUpgradeEffect(upgradeId, currentLevel);

    switch (effectType) {
      case 'laser_flat_base':
        return `+${totalEffect.toFixed(1)} damage`;
      case 'laser_mult_base':
      case 'laser_dmg_mult':
        return `+${(totalEffect * 100).toFixed(1)}% damage`;
      case 'mine_radius_mult':
        return `+${(totalEffect * 100).toFixed(1)}% radius`;
      case 'crit_chance':
        return `+${(totalEffect * 100).toFixed(1)}% crit chance`;
      case 'crit_mult_bonus':
        return `+${(totalEffect * 100).toFixed(1)}% crit damage`;
      case 'craft_double_chance':
        return `+${(totalEffect * 100).toFixed(1)}% double chance`;
      case 'craft_speed_mult':
        return `+${(totalEffect * 100).toFixed(1)}% speed`;
      case 'triple_rock_chance':
        return `+${(totalEffect * 100).toFixed(1)}% triple chance`;
      case 'ore_sell_mult':
        return `+${(totalEffect * 100).toFixed(1)}% sell value`;
      case 'bomb_dmg_mult':
        return `+${(totalEffect * 100).toFixed(1)}% bomb damage`;
      case 'bomb_radius_mult':
        return `+${(totalEffect * 100).toFixed(1)}% bomb radius`;
      case 'energy_max':
        return `+${totalEffect.toFixed(0)} max energy`;
      case 'energy_regen_mult':
        return `+${(totalEffect * 100).toFixed(1)}% regen`;
      case 'coin_mult':
        return `+${(totalEffect * 100).toFixed(1)}% coins`;
      case 'exp_mult':
        return `+${(totalEffect * 100).toFixed(1)}% exp`;
      case 'attack_speed':
        return `+${(totalEffect * 100).toFixed(1)}% attack speed`;
      case 'supercrit_chance':
        return `+${(totalEffect * 100).toFixed(1)}% super crit chance`;
      case 'supercrit_mult_bonus':
        return `+${(totalEffect * 100).toFixed(1)}% super crit damage`;
      case 'ultracrit_chance':
        return `+${(totalEffect * 100).toFixed(1)}% ultra crit chance`;
      case 'bomb_ultracrit_chance':
        return `+${(totalEffect * 100).toFixed(1)}% bomb ultra crit chance`;
      case 'bomb_crit_mult_bonus':
        return `+${(totalEffect * 100).toFixed(1)}% bomb crit damage`;
      case 'craft_triple_chance':
        return `+${(totalEffect * 100).toFixed(1)}% triple craft chance`;
      case 'laser_dmg_per_card':
        return `+${(totalEffect * 100).toFixed(1)}% damage per card`;
      case 'upgrade_coin_cost_mult':
        return `${(totalEffect * 100).toFixed(1)}% cheaper upgrades`;
      case 'enemy_armor_reduction':
        return `${(totalEffect * 100).toFixed(1)}% less enemy armor`;
      case 'meter_fill_rate_mult':
        return `×${totalEffect.toFixed(1)} meter fill rate`;
      case 'upg_cap_plus':
        return `+${totalEffect.toFixed(0)} upgrade cap`;
      case 'bomb_recharge_rate':
        return `+${(totalEffect * 100).toFixed(1)}% recharge rate`;
      case 'free_bomb_chance':
        return `+${(totalEffect * 100).toFixed(1)}% free bomb chance`;
      default:
        return `+${totalEffect.toFixed(1)}`;
    }
  }

  function handleUpgradeClick(upgradeId: string) {
    const success = Upgrades.purchaseUpgrade(upgradeId);
    if (success) {
      console.log('✅ Debug: Upgrade purchased successfully, triggering update');
      upgradeTrigger++; // Принудительно обновляем список апгрейдов
      onUpgradePurchased({ upgradeId });
    } else {
      console.log('❌ Debug: Upgrade purchase failed');
    }
  }

  function handleClose() {
    onClose();
  }

  function animateUpgradeButton(event: Event) {
    const button = event.currentTarget as HTMLElement;
    if (button) {
      animations.upgradePurchase(button);
    }
  }
</script>

<Modal {isOpen} title="Upgrade Shop" size="lg" maxHeightVh={85} on:close={handleClose}>
  <div class="upgrades-container">
    <!-- Currency Panel -->
    <div class="currency-header">
      <CurrencyPanel size="md" showLabel={true} />
    </div>

    {#if allUpgrades.length === 0}
      <div class="empty-state">
        <div class="empty-icon">🔒</div>
        <h3>No Upgrades Available</h3>
        <p>Reach higher levels to unlock more upgrades!</p>
      </div>
    {:else}
      <div class="upgrades-list">
        {#each allUpgrades as upgrade (upgrade.id)}
          <div class="upgrade-item">
            <div class="upgrade-main">
              <div class="upgrade-info">
                <div class="upgrade-title">
                  <h4>{upgrade.label}</h4>
                  <span class="upgrade-level"
                    >Lv.{upgrade.currentLevel}/{upgrade.config.max_lvl}</span
                  >
                </div>
                <div class="upgrade-effect">
                  {upgrade.effectText}
                </div>
              </div>

              <div class="upgrade-actions">
                <div class="upgrade-cost">
                  <CostDisplay
                    cost={upgrade.cost}
                    size="sm"
                    showIcons={true}
                    showLabels={false}
                    compact={true}
                    showCurrentTotal={true}
                  />
                </div>

                <div
                  class="upgrade-button-wrapper"
                  class:upgrade-button--disabled={!upgrade.canAfford && !upgrade.isMaxLevel}
                >
                  <Button
                    variant={upgrade.canAfford
                      ? 'success'
                      : upgrade.isMaxLevel
                        ? 'primary'
                        : 'primary'}
                    disabled={!upgrade.canAfford || upgrade.isMaxLevel}
                    size="sm"
                    on:click={e => {
                      animateUpgradeButton(e);
                      handleUpgradeClick(upgrade.id);
                    }}
                  >
                    {upgrade.isMaxLevel ? 'Max' : 'Upgrade'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</Modal>

<style lang="postcss">
  @reference "@/styles/theme.css";

  .upgrades-container {
    @apply w-full h-full;
  }

  .currency-header {
    @apply flex justify-center mb-4;
  }

  .empty-state {
    @apply flex flex-col items-center justify-center;
    @apply py-16 px-8;
    @apply text-center;
  }

  .empty-icon {
    @apply text-6xl mb-4;
    @apply opacity-50;
  }

  .empty-state h3 {
    @apply text-xl font-bold text-neon-cyan mb-2;
  }

  .empty-state p {
    @apply text-gray-400;
  }

  .upgrades-list {
    @apply space-y-2;
    @apply max-h-96 overflow-y-auto;
    @apply pr-2;
  }

  .upgrade-item {
    @apply bg-dark-bg/50;
    @apply border border-neon-green/20;
    @apply rounded-lg;
    @apply p-3;
    @apply transition-all duration-200;
    @apply hover:border-neon-green/40;
    @apply hover:bg-dark-bg/70;
  }

  .upgrade-main {
    @apply flex justify-between items-center;
    @apply gap-3;
  }

  .upgrade-info {
    @apply flex-1;
  }

  .upgrade-title {
    @apply flex items-center gap-2 mb-1;
  }

  .upgrade-title h4 {
    @apply text-sm font-bold text-neon-cyan;
  }

  .upgrade-level {
    @apply text-xs text-gray-400;
    @apply font-mono;
    @apply bg-gray-800/50;
    @apply px-1.5 py-0.5;
    @apply rounded;
  }

  .upgrade-effect {
    @apply text-xs font-bold;
    @apply text-neon-green;
    @apply bg-neon-green/10;
    @apply px-1.5 py-0.5;
    @apply rounded;
    @apply font-mono;
    @apply inline-block;
  }

  .upgrade-actions {
    @apply flex items-center gap-2;
  }

  .upgrade-cost {
    @apply text-right;
  }

  .upgrade-button-wrapper {
    @apply transition-all duration-200;
  }

  .upgrade-button--disabled {
    @apply opacity-40;
    @apply cursor-not-allowed;
  }

  .upgrade-button--disabled :global(button) {
    @apply bg-gray-600/30;
    @apply border-gray-600/30;
    @apply text-gray-400;
  }

  .upgrade-button--disabled:hover :global(button) {
    @apply bg-gray-600/30;
    @apply border-gray-600/30;
    @apply text-gray-400;
    @apply transform-none;
  }

  /* Scrollbar styling */
  .upgrades-list::-webkit-scrollbar {
    @apply w-2;
  }

  .upgrades-list::-webkit-scrollbar-track {
    @apply bg-dark-bg/30;
    @apply rounded;
  }

  .upgrades-list::-webkit-scrollbar-thumb {
    @apply bg-neon-green/30;
    @apply rounded;
  }

  .upgrades-list::-webkit-scrollbar-thumb:hover {
    @apply bg-neon-green/50;
  }
</style>
